"use client";

import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useLocale } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { useEffect, useMemo } from "react";
import FullLoadingView from "@/components/extended/FullLoadingView";
import { cn } from "@/lib/utils";
import { appShellInnerClassName } from "@/lib/appShell";

// Route map: path → lazy-loaded component
const routes: Record<string, ReturnType<typeof dynamic>> = {
  "/home": dynamic(() => import("../home/PageView")),
  "/category": dynamic(() => import("../category/PageView")),
  "/activity": dynamic(() => import("../activity/Client")),
  "/pc": dynamic(() => import("../pc/PageView")),
  "/topic": dynamic(() => import("../topic/Client")),
  "/shoppingCart": dynamic(() => import("../shoppingCart/PageView")),

  // My
  "/my": dynamic(() => import("../my/PageView")),
  "/my/collect": dynamic(() => import("../my/collect/PageView")),
  "/my/companyInfo": dynamic(() => import("../my/companyInfo/PageView")),
  "/my/contactList": dynamic(() => import("../my/contactList/PageView")),
  "/my/coupon": dynamic(() => import("../my/coupon/PageView")),
  "/my/serviceStore": dynamic(() => import("../my/serviceStore/PageView")),
  "/my/setting": dynamic(() => import("../my/setting/PageView")),
  "/my/setting/account": dynamic(
    () => import("../my/setting/account/PageView"),
  ),
  "/my/setting/privacyAndAgreement": dynamic(
    () => import("../my/setting/privacyAndAgreement/PageView"),
  ),

  // Account
  "/account/login": dynamic(() => import("../account/login/Client")),
  "/account/create": dynamic(() => import("../account/create/PageView")),
  "/account/create/success": dynamic(
    () => import("../account/create/success/PageView"),
  ),
  "/account/reset": dynamic(() => import("../account/reset/Client")),
  "/account/reset/modify": dynamic(
    () => import("../account/reset/modify/Client"),
  ),
  "/account/reset/verification": dynamic(
    () => import("../account/reset/verification/Client"),
  ),

  // Address
  "/address/edit": dynamic(() => import("../address/edit/Client")),
  "/address/list": dynamic(() => import("../address/list/PageView")),

  // Bill
  "/bill/list": dynamic(() => import("../bill/list/PageView")),
  "/bill/detail": dynamic(() => import("../bill/detail/[id]/Client")),

  // Order
  "/order/confirm": dynamic(() => import("../order/confirm/Client")),
  "/order/detail": dynamic(() => import("../order/detail/Client")),
  "/order/list": dynamic(() => import("../order/list/Client")),
  "/order/packages": dynamic(() => import("../order/packages/PageView")),
  "/order/payResult": dynamic(() => import("../order/payResult/Client")),

  // Product
  "/product/detail": dynamic(() => import("../product/detail/Client")),
  "/product/list": dynamic(() => import("../product/list/Client")),

  // Refund
  "/refund/detail": dynamic(() => import("../refund/detail/Client")),
  "/refund/list": dynamic(() => import("../refund/list/PageView")),
};

export default function CatchAllPage() {
  const pathname = usePathname();
  const locale = useLocale();
  const router = useRouter();

  // Strip locale prefix to get the route path
  const routePath = useMemo(() => {
    let stripped = pathname.replace(`/${locale}`, "") || "/";
    // 去掉尾部斜杠（trailingSlash: true 会让 pathname 带尾部 /）
    if (stripped !== "/" && stripped.endsWith("/")) {
      stripped = stripped.slice(0, -1);
    }
    return stripped;
  }, [pathname, locale]);

  // 与 app/page.tsx 一致：语言根路径进入首页
  useEffect(() => {
    if (routePath === "/" || routePath === "") {
      router.replace("/home");
    }
  }, [routePath, router]);

  if (routePath === "/" || routePath === "") {
    return <FullLoadingView />;
  }

  // Try exact match first
  let Component = routes[routePath];

  // Try prefix match for dynamic routes (e.g., /bill/detail/123 → /bill/detail)
  if (!Component) {
    const segments = routePath.split("/").filter(Boolean);
    for (let i = segments.length - 1; i >= 1; i--) {
      const prefix = "/" + segments.slice(0, i).join("/");
      if (routes[prefix]) {
        Component = routes[prefix];
        break;
      }
    }
  }

  if (!Component) {
    return (
      <div className="min-h-screen bg-muted">
        <div className={cn(appShellInnerClassName, "min-h-screen bg-background")}>
          <div className="flex min-h-screen items-center justify-center">
            <p>404 - Page not found</p>
          </div>
        </div>
      </div>
    );
  }

  const isLoginFullBleed = routePath === "/account/login";

  return (
    <div className="min-h-screen bg-muted">
      {isLoginFullBleed ? (
        <Component />
      ) : (
        <div className={cn(appShellInnerClassName, "min-h-screen bg-background")}>
          <Component />
        </div>
      )}
    </div>
  );
}
